import type { DashboardSnapshot } from '@dash/shared';
import { Card } from '../components/Card.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { StackedBar } from '../components/StackedBar.tsx';
import { SectionHeader } from '../components/SectionHeader.tsx';
import { fmtInt, fmtPercent, fmtCurrency } from '../lib/format.ts';

interface ComercialProps {
  snap: DashboardSnapshot;
}

export function ComercialView({ snap }: ComercialProps) {
  const { negocio } = snap;
  const inadTone = negocio.inadimplencia.percent > 8 ? 'crit' : negocio.inadimplencia.percent > 5 ? 'warn' : 'ok';
  const cancTone = negocio.cancelamentos > 30 ? 'crit' : negocio.cancelamentos > 20 ? 'warn' : 'ok';

  const saldo = negocio.vendasMes - negocio.cancelamentos;
  const saldoTone = saldo > 0 ? 'ok' : saldo === 0 ? 'neutral' : 'crit';

  return (
    <div className="grid gap-4 p-6 lg:grid-cols-12">
      {/* HERO — Vendas mes vs meta */}
      <SectionHeader label="metas do mês" accent="ok" />
      <div className="col-span-12 lg:col-span-8">
        <ProgressBar
          label="vendas no mês vs meta"
          value={negocio.vendasMes}
          target={negocio.metaMes}
          hint="vendas brutas"
        />
      </div>

      <div className="col-span-12 grid grid-cols-2 gap-4 lg:col-span-4 lg:auto-rows-min lg:grid-cols-1">
        <Card title="Vendas hoje" tone="ok" value={fmtInt(negocio.vendasDia)} size="lg" hint="dia atual" />
        <Card title="Saldo líquido" tone={saldoTone} value={fmtInt(saldo)} size="lg" hint="vendas − cancel." />
      </div>

      {/* OS */}
      <SectionHeader label="ordens de serviço" accent="info" />
      <div className="col-span-12 lg:col-span-8">
        <ProgressBar
          label="OS concluídas hoje vs meta"
          value={negocio.osConcluidas}
          target={negocio.osMeta}
          hint="meta diária"
        />
      </div>

      <div className="col-span-12 grid grid-cols-2 gap-4 lg:col-span-4 lg:grid-cols-1">
        <Card title="Cancelamentos" tone={cancTone} value={fmtInt(negocio.cancelamentos)} size="lg" hint="mês acumulado" />
        <Card
          title="Churn mês"
          tone={cancTone}
          value={fmtPercent(negocio.vendasMes > 0 ? (negocio.cancelamentos / negocio.vendasMes) * 100 : 0)}
          size="lg"
          hint="cancel. / vendas"
        />
      </div>

      {/* Inadimplência */}
      <SectionHeader label="inadimplência" hint="por idade" accent="warn" />
      <div className="col-span-12 lg:col-span-8">
        <StackedBar
          title="distribuição por idade (clientes em aberto)"
          format="int"
          segments={[
            { label: 'até 30d', value: negocio.inadimplencia.porIdade.ate30, colorClass: 'bg-[--color-info]' },
            { label: '31-60d', value: negocio.inadimplencia.porIdade.de31a60, colorClass: 'bg-[--color-warn]' },
            { label: '61-90d', value: negocio.inadimplencia.porIdade.de61a90, colorClass: 'bg-[--color-warn]/70' },
            { label: '90d+', value: negocio.inadimplencia.porIdade.acima90, colorClass: 'bg-[--color-crit]' },
          ]}
        />
      </div>

      <div className="col-span-12 grid grid-cols-2 gap-4 lg:col-span-4 lg:grid-cols-1">
        <Card title="Inadimplência %" tone={inadTone} value={fmtPercent(negocio.inadimplencia.percent)} size="lg" hint="sobre base" />
        <Card title="Valor em aberto" tone={inadTone} value={fmtCurrency(negocio.inadimplencia.valor)} size="md" hint="aprox." />
      </div>
    </div>
  );
}
